import { Injectable, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { SiaI18nApi } from '../apis/sia-i18n-api';
import { SiaTokenUser } from '../interfaces/sia-token-user';
import { SiaTranslations } from '../interfaces/sia-translations';
import { SiaUsersService } from './users.service';

@Injectable({
  providedIn: 'root',
})
export class SiaI18nService {
  static instance: SiaI18nService;

  #userService = inject(SiaUsersService);
  #i18nApi = inject(SiaI18nApi);

  language?: string | null;

  #translations: Record<string, string> = {};
  #subs: Subscription[] = [];
  #user: SiaTokenUser | null = null;
  #initialized = false;

  #zeroWidthBoundary = '\u200B';
  #translationPattern = /\u200B(.+?)\u200B/g;

  // Patterns for finding and extracting {{nt}} tags
  #ntCheckPattern = /\{\{[Nn][Tt]\}\}/;

  // Expanded selector to also find {{nt}} and {{Nt}} inside placeholders
  #placeholderSelector =
    '[placeholder*="\u200B"]:not([placeholder*="\u200C"]), [placeholder*="{{nt}}"]:not([placeholder*="\u200C"]), [placeholder*="{{Nt}}"]:not([placeholder*="\u200C"])';

  #processedMarker = '\u200C';

  #isProcessed(value: string): boolean {
    return value.includes(this.#processedMarker);
  }

  #stripProcessedMarker(value: string): string {
    return value.replaceAll(this.#processedMarker, '');
  }

  #markProcessed(value: string): string {
    // prevent accumulation
    const cleaned = this.#stripProcessedMarker(value);
    return cleaned + this.#processedMarker;
  }

  get user(): SiaTokenUser | null {
    if (!this.#user) {
      this.#user = this.#userService.getUserData();
    }
    return this.#user;
  }

  constructor() {
    SiaI18nService.instance = this;
    this.initialize();
  }

  private async initialize() {
    if (this.#initialized) return;
    this.#initialized = true;

    const observer = new MutationObserver((mutations) => {
      try {
        this.translateDom(mutations);
      } catch (e) {
        console.error('[SiaI18nService]', e);
      }
    });
    observer.observe(document, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['placeholder'],
    });

    document.addEventListener('visibilitychange', () => {
      try {
        if (document.visibilityState === 'visible') {
          this.translateDom();
        }
      } catch (e) {
        console.error('[SiaI18nService]', e);
      }
    });
    window.addEventListener('pageshow', (event: PageTransitionEvent) => {
      try {
        if (event.persisted) {
          this.translateDom();
        }
      } catch (e) {
        console.error('[SiaI18nService]', e);
      }
    });

    await this.updateSessionTranslations();

    this.translateDom();

    const s1 = this.#userService.userChanged$.subscribe(
      async (data: SiaTokenUser) => {
        this.#user = data;
        await this.updateSessionTranslations();
        this.translateDom();
        this.handleUserChange();
      },
    );
    this.#subs.push(s1);
  }

  private handleUserChange() {
    window.location.reload();
  }

  async updateSessionTranslations() {
    this.language = this.#userService.getLanguage();
    if (!this.language) {
      this.language = navigator.language?.split('-')[0].toUpperCase() || 'IT';
    }
    const res = await this.#i18nApi.getTranslationsFromDB(
      this.language ?? 'IT',
    );

    const translations: Record<string, string> = {};
    for (const curr of res) {
      if (curr.italianText && curr.translatedText) {
        translations[curr.italianText.trim()] = curr.translatedText.replaceAll(
          '$$',
          this.#zeroWidthBoundary,
        );
      }
    }
    this.#translations = translations;
  }

  private translateDom = (mutations?: MutationRecord[]) => {
    if (!this.language) {
      return;
    }

    if (!mutations || mutations.length === 0) {
      const root = document.getElementById('app') || document.body;
      if (!root) return;
      this.#translateNode(root);
      return;
    }

    const nodesToProcess = new Set<Node>();

    for (const mutation of mutations) {
      if (mutation.type === 'characterData') {
        nodesToProcess.add(mutation.target);
        continue;
      }

      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => nodesToProcess.add(node));
      }

      if (mutation.type === 'attributes') {
        nodesToProcess.add(mutation.target);
      }
    }

    if (nodesToProcess.size === 0) {
      return;
    }

    // Optimization: Prune child nodes if their ancestor is already queued.
    // This prevents deep redundant tree walks on large DOM insertions.
    const uniqueRoots = new Set<Node>();
    nodesToProcess.forEach((node) => {
      let parent = node.parentNode;
      let hasQueuedAncestor = false;
      while (parent) {
        if (nodesToProcess.has(parent)) {
          hasQueuedAncestor = true;
          break;
        }
        parent = parent.parentNode;
      }
      if (!hasQueuedAncestor) {
        uniqueRoots.add(node);
      }
    });

    uniqueRoots.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        this.#translateNode(node);
        return;
      }

      if (this.#isQuerySelectorRoot(node)) {
        this.#translateNode(node);
        return;
      }

      if (node.parentNode) {
        this.#translateNode(node.parentNode);
      }
    });
  };

  #translateNode(node: Node) {
    if (node.nodeType === Node.TEXT_NODE) {
      this.#processTextNode(node as Text);
      return;
    }

    if (!this.#isQuerySelectorRoot(node)) {
      return;
    }

    const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT, {
      acceptNode: (current) => {
        const txt = current.nodeValue || '';

        // Must contain either standard boundaries or safety check {{nt}} markers
        if (
          !txt.includes(this.#zeroWidthBoundary) &&
          !this.#ntCheckPattern.test(txt)
        ) {
          return NodeFilter.FILTER_SKIP;
        }

        // if already processed, skip to avoid re-processing loops
        if (this.#isProcessed(txt)) {
          return NodeFilter.FILTER_SKIP;
        }

        return NodeFilter.FILTER_ACCEPT;
      },
    });

    let textNode = walker.nextNode();
    while (textNode) {
      this.#processTextNode(textNode as Text);
      textNode = walker.nextNode();
    }

    if (node instanceof Element) {
      this.#processPlaceholder(node);
    }

    if (
      node instanceof Element ||
      node instanceof Document ||
      node instanceof DocumentFragment
    ) {
      const placeholders = node.querySelectorAll(this.#placeholderSelector);
      placeholders.forEach((el) => this.#processPlaceholder(el));
    }
  }

  // Unified logic to handle both Boundaries and {{nt}} tags efficiently
  #processContent(originalRaw: string | null): string | null {
    if (!originalRaw) return null;

    const hasBoundary = originalRaw.includes(this.#zeroWidthBoundary);
    const hasNtMarker = this.#ntCheckPattern.test(originalRaw);

    if (!hasBoundary && !hasNtMarker) {
      return null;
    }

    if (this.#isProcessed(originalRaw)) {
      return null;
    }

    let processedText = this.#stripProcessedMarker(originalRaw);

    if (hasBoundary) {
      processedText = processedText.replace(
        this.#translationPattern,
        (_match, key: string) => this.translate(key),
      );
    }

    if (this.#ntCheckPattern.test(processedText)) {
      processedText = this.translateMixed(processedText);
    }

    return this.#markProcessed(processedText);
  }

  #processTextNode(node: Text) {
    const result = this.#processContent(node.nodeValue);
    if (result !== null) {
      node.nodeValue = result;
    }
  }

  #processPlaceholder(element: Element) {
    const result = this.#processContent(element.getAttribute('placeholder'));
    if (result !== null) {
      element.setAttribute('placeholder', result);
    }
  }

  #isQuerySelectorRoot(
    node: Node,
  ): node is Document | Element | DocumentFragment {
    return (
      node instanceof Document ||
      node instanceof Element ||
      node instanceof DocumentFragment
    );
  }

  getItalianText(text: string, language: string): Promise<string | null> {
    return this.#i18nApi.getItalianText(text, language);
  }

  translate(toTranslate: string, startingLanguage?: string): string {
    if (!this.language || typeof toTranslate !== 'string') {
      return toTranslate;
    }

    if (
      this.language === 'IT' &&
      startingLanguage &&
      startingLanguage !== 'IT'
    ) {
      const italianText = this.#i18nApi.getItalianTextSync(
        toTranslate,
        startingLanguage,
      );
      if (italianText) {
        return italianText;
      }
    }

    if (this.#ntCheckPattern.test(toTranslate)) {
      return this.translateMixed(toTranslate, startingLanguage);
    }

    const translated = this.#translations[toTranslate.trim()];
    return translated ?? this.translationNotFound(toTranslate);
  }

  private translateMixed(text: string, startingLanguage?: string): string {
    // 1. Use [\s\S]*? to support multi-line text (newlines inside the tag)
    // 2. Use \/[Nn][Tt] to support both {{/nt}} and {{/Nt}} closing tags
    const pattern = /(\w*)\{\{([Nn][Tt])\}\}([\s\S]*?)\{\{\/[Nn][Tt]\}\}/g;
    let result = '';
    let lastIndex = 0;
    let hasMatch = false;

    for (const match of text.matchAll(pattern)) {
      hasMatch = true;
      const wordPrefix = match[1];
      const matchIndex = match.index!;

      const textBefore = text.substring(lastIndex, matchIndex);
      if (textBefore.trim()) {
        const trimmed = textBefore.trim();
        const translated = this.translate(trimmed, startingLanguage);
        const leadingWhitespace = textBefore.match(/^\s*/)?.[0] ?? '';
        const trailingWhitespace = textBefore.match(/\s*$/)?.[0] ?? '';
        result += leadingWhitespace + translated + trailingWhitespace;
      } else {
        result += textBefore;
      }

      if (wordPrefix) {
        result += this.translate(wordPrefix, startingLanguage);
      }

      const openingTag = match[2];
      const shouldCapitalize = openingTag[0] === 'N';
      let noTranslateText = match[3];

      if (shouldCapitalize && noTranslateText.length > 0) {
        noTranslateText =
          noTranslateText.charAt(0).toUpperCase() + noTranslateText.slice(1);
      }

      result += noTranslateText;

      lastIndex = matchIndex + match[0].length;
    }

    // 3. Fallback: Prevent infinite recursion if a tag is malformed/unclosed.
    // If text contains {{nt}} but doesn't match the valid pair pattern,
    // we safely remove the broken tags and translate the rest to prevent stack overflow.
    if (!hasMatch) {
      const cleanedText = text.replace(/\{\{\/?[Nn][Tt]\}\}/g, '');
      return this.translate(cleanedText, startingLanguage);
    }

    const remainingText = text.substring(lastIndex);
    if (remainingText.trim()) {
      const trimmed = remainingText.trim();
      const translated = this.translate(trimmed, startingLanguage);
      const leadingWhitespace = remainingText.match(/^\s*/)?.[0] ?? '';
      const trailingWhitespace = remainingText.match(/\s*$/)?.[0] ?? '';
      result += leadingWhitespace + translated + trailingWhitespace;
    } else {
      result += remainingText;
    }

    return result;
  }

  private translationNotFound(toTranslate: string) {
    toTranslate = toTranslate.trim();
    if (
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        toTranslate,
      )
    ) {
      return toTranslate;
    }
    let tran: SiaTranslations = {
      italianText: toTranslate,
      translatedText: '$$' + toTranslate + '$$',
      languageId: this.language || 'EN',
    };
    this.#i18nApi.createRemoteTranslation(tran);
    this.#translations = { ...this.#translations, [toTranslate]: toTranslate };
    return this.#zeroWidthBoundary + toTranslate + this.#zeroWidthBoundary;
  }
}
